import React, {useEffect, useState} from "react";

import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {Message} from "primereact/message";

import {Loading} from "../../utils/InlineComponents";
import {AppContext} from "../../HomePage";
import {DynamicPricingTemplateService} from "../../service/DynamicPricingTemplateService";
import RuleDetails from "./RuleDetails";

const templateService = new DynamicPricingTemplateService();

// Bulk-apply flow (Task B4): opened from the product selection bar to apply a
// single dynamic pricing template to the currently selected products. The
// backend enqueues a job and returns immediately, so a successful apply is
// reported as "queued / running in background" rather than "done".
const ApplyTemplateDialog = ({visible, productIds, onHide, onApplied, t}) => {

    // Fall back to AppContext for the shared toast / setError helpers when they
    // are not passed directly as props.
    const {toast, setError} = React.useContext(AppContext) || {};

    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const reportError = (error) => {
        if (setError) {
            setError(error);
            setError(null);
        }
    };

    const showSuccess = (summary, detail) => {
        if (toast && toast.current)
            toast.current.show({severity: 'success', summary, detail});
    };

    // (Re)load the template list and reset the picker every time the dialog opens.
    useEffect(() => {
        if (!visible) return;
        setSelectedTemplateId(null);
        setLoading(true);
        templateService.list().then(data => {
            setTemplates(data || []);
        }).catch(reportError).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    const selectedTemplate = templates.find(tpl => tpl.id === selectedTemplateId) || null;

    const productCount = (productIds || []).length;
    const canApply = !submitting && !!selectedTemplateId && productCount > 0;

    const apply = () => {
        if (!canApply) return;
        setSubmitting(true);
        templateService.apply(selectedTemplateId, productIds).then((res) => {
            if (res && res.success === false) {
                if (setError) setError(new Error(res.error || 'error'));
                if (onApplied) onApplied([]);
            } else {
                showSuccess(t('pricing_template.apply'), t('pricing_template.apply_done'));
                if (onApplied) onApplied(res && res.products);
            }
            if (onHide) onHide();
        }).catch(reportError).finally(() => setSubmitting(false));
    };

    const templateOptions = templates.map(tpl => ({label: tpl.name, value: tpl.id}));

    return (
        <Dialog header={t('pricing_template.apply_title')} visible={visible}
                style={{width: '480px', maxWidth: '95vw'}} modal
                onHide={() => !submitting && onHide && onHide()}
                footer={
                    <div>
                        <Button label={t('actions.cancel')} className="p-button-text"
                                disabled={submitting} onClick={() => onHide && onHide()}/>
                        <Button label={t('pricing_template.apply')} icon="pi pi-check"
                                loading={submitting} disabled={!canApply} onClick={apply}/>
                    </div>
                }>
            {loading
                ? <div style={{textAlign: 'center'}}><Loading/></div>
                : templates.length === 0
                    ? <Message severity="info" style={{display: 'block'}}
                               text={t('pricing_template.none_create_first')}/>
                    : <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                        <small style={{color: '#6b7280'}}>
                            {t('pricing_template.products_selected', {count: productCount})}
                        </small>
                        <Dropdown value={selectedTemplateId} options={templateOptions}
                                  onChange={(e) => setSelectedTemplateId(e.value)}
                                  placeholder={t('pricing_template.select_placeholder')}
                                  style={{width: '100%'}}/>
                        {selectedTemplate &&
                            <div style={{border: '1px solid #e5e7eb', borderRadius: 10,
                                padding: 16, background: '#fff'}}>
                                <RuleDetails rule={selectedTemplate} t={t} showHeader/>
                            </div>}
                    </div>}
        </Dialog>
    );
};

export default ApplyTemplateDialog;
